import {
  WaterQualityReading,
  WaterQualityThreshold,
  WaterQualityColor,
  WaterQualityStatus,
  getWaterQualityStatus,
  extractReadingValueFromSensorEvent,
  SensorType,
} from "common";
import React from "react";
import { useTranslation } from "react-i18next";
import { tokens, gradients } from "tamagui-config";

import { useLatestSensorEvents } from "../sensors/useLatestSensorEvents";

export const waterQualityColorTokens = {
  [WaterQualityColor.Red]: {
    gradientStart: tokens.color.warningRed,
    gradientEnd: tokens.color.warningRed,
    backgroundGradients: gradients.error,
  },
  [WaterQualityColor.Yellow]: {
    gradientStart: tokens.color.warning,
    gradientEnd: tokens.color.warning,
    backgroundGradients: gradients.warning,
  },
  [WaterQualityColor.Blue]: {
    gradientStart: tokens.color.waterGradientStart,
    gradientEnd: tokens.color.waterGradientEnd,
    backgroundGradients: gradients.blue,
  },
};

export interface WaterQualityResult {
  data: WaterQualityStatus | null;
  loading: boolean;
  error: Error | null;
}

export const useGetWaterQualityData = (): WaterQualityResult => {
  const { t } = useTranslation();
  const { event, inProgress } = useLatestSensorEvents(SensorType.WATER_QUALITY);

  const processData = React.useMemo(() => {
    if (!event) {
      return { data: null, error: null };
    }

    try {
      const value =
        extractReadingValueFromSensorEvent<WaterQualityReading>(event)?.ec;
      if (value === undefined) {
        return { data: null, error: null };
      }

      const status = getWaterQualityStatus(value, (threshold) => {
        switch (threshold) {
          case WaterQualityThreshold.excellent:
            return t("water.quality.excellent");
          case WaterQualityThreshold.good:
            return t("water.quality.good");
          case WaterQualityThreshold.fair:
            return t("water.quality.fair");
          case WaterQualityThreshold.poor:
            return t("water.quality.poor");
          case WaterQualityThreshold.veryPoor:
            return t("water.quality.veryPoor");
        }
      });

      return { data: status, error: null };
    } catch (err) {
      console.error("Error processing water quality data:", err);
      return { data: null, error: err as Error };
    }
  }, [event, t]);

  return {
    data: processData.data,
    loading: inProgress,
    error: processData.error,
  };
};
